import { message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { getRouteMyRoutesThunk } from "tm-shared/api";

import { ROLE_CHILDREN_MAP } from "@/constants";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { MainContainer } from "@/shared/layout/Layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";

import {
    getRouteManagementBlocks,
    RouteManagementHeader,
    RouteManagementModel,
} from "./RouteManagement.blocks";
import { getRouteManagementCapabilities } from "./RouteManagement.policy";

const RouteManagement = () => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const offlineMode = useOfflineModeSelector();
    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);

    const capabilities = useMemo(
        () => getRouteManagementCapabilities(role),
        [role],
    );

    useEffect(() => {
        const fetchMyRoutes = async () => {
            if (!offlineMode) {
                try {
                    setIsLoading(true);

                    await dispatch(
                        getRouteMyRoutesThunk({
                            ownerId: userId,
                            roleSlug: ROLE_CHILDREN_MAP[role]!,
                        }),
                    );
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchMyRoutes();
    }, [dispatch, offlineMode, userId, role]);

    const model: RouteManagementModel = useMemo(
        () => ({
            capabilities,
            isLoading,
            isOffline: offlineMode,
            messageApi,
        }),
        [capabilities, isLoading, offlineMode, messageApi],
    );

    const blocks = useMemo(() => getRouteManagementBlocks(model), [model]);

    return (
        <>
            <Meta title="Routes editor" />
            <RouteManagementHeader
                isLoading={isLoading}
                messageApi={messageApi}
                canExportRoutes={capabilities.canExportRoutes}
            />
            {contextHolder}
            <MainContainer>
                <BlockList blocks={blocks} model={model} />
            </MainContainer>
        </>
    );
};

export default RouteManagement;
